import { Fragment } from 'react';
import { ChevronRight, RotateCcw, Trash2 } from 'lucide-react';
import { scoreColor, timeOfDay, dayKey, dayShort, dayHeading, formatTime, groupHistory, normHistItem } from './helpers';

// Vue liste d'historique partagée : toolbar (filtre/tri) + timeline (jour) OU classement (score).
// readOnly = aperçu suivi (sans boutons d'action ni suppression).
// expandedGroups/toggleGroup restent chez le parent pour conserver les dépliages entre vues.
export default function HistoryView({
    entries, filter, setFilter, sort, setSort,
    readOnly = false, onClear = null,
    expandedGroups, toggleGroup, onConsulter, onRefaire,
    onReview = null, // lecture seule : « Revoir » une séance (espace formateur), reçoit l'entrée d'historique
    activeKey = null, // ligne sur laquelle on vient d'agir : encadrée
}) {
    // Carte d'un quiz, réutilisée par la timeline (variant 'timeline') et le classement (variant 'rank').
    const renderHistCard = (it, variant, rank) => {
        const col = scoreColor(it.pct);
        const isApp = it.mode === 'apprentissage';
        const expanded = it.kind === 'group' && expandedGroups.has(it.group.session_group);
        const meta = variant === 'rank'
            ? `${it.score}/${it.total} · ${dayShort(it.created_at)} ${timeOfDay(it.created_at)}${it.meta ? ` · ${it.meta}` : ''}`
            : `${timeOfDay(it.created_at)} · ${formatTime(it.time)}${it.meta ? ` · ${it.meta}` : ''}`;
        return (
            <div className={`hist-card ${variant === 'rank' ? 'hist-card--rank' : ''} ${expanded ? 'hist-card--open' : ''} ${activeKey === it.key ? 'hist-card--active' : ''}`} style={variant === 'rank' ? { borderLeftColor: col } : undefined}>
                <span className="hist-card__expand">
                    {it.kind === 'group' && (
                        <button type="button" className="hist-card__expand-btn" onClick={() => toggleGroup(it.group.session_group)} aria-label={expanded ? 'Réduire' : 'Voir le détail'} title={expanded ? 'Réduire' : 'Voir le détail'}>
                            <ChevronRight size={15} style={{ transform: expanded ? 'rotate(90deg)' : 'none', transition: 'transform 0.15s ease' }} />
                        </button>
                    )}
                </span>
                {variant === 'rank' && <span className="hist-card__rank">{rank}</span>}
                {variant === 'rank' && <span className="hist-card__bar"><span style={{ width: `${it.pct}%`, background: col }} /></span>}
                {variant === 'rank' && <span className="hist-card__pct hist-card__pct--lg" style={{ color: col }}>{it.pct}%</span>}
                <div className="hist-card__main">
                    <div className="hist-card__titleline">
                        <span className="hist-card__title">{it.title}</span>
                        <span className={`hist-badge hist-badge--${isApp ? 'app' : 'classic'}`}>{isApp ? 'Apprentissage' : 'Classique'}</span>
                    </div>
                    <div className="hist-card__meta">{meta}</div>
                    {it.conditions?.length > 0 && (
                        <div className="hist-card__conds">
                            {it.conditions.map(c => (
                                <span key={c.key} className={`hist-cond ${c.ok === true ? 'is-ok' : c.ok === false ? 'is-ko' : ''}`}>{c.text}</span>
                            ))}
                        </div>
                    )}
                </div>
                {variant !== 'rank' && (
                    <div className="hist-card__score">
                        <span className="hist-card__pct" style={{ color: col }}>{it.pct}%</span>
                        <span className="hist-card__frac">{it.score}/{it.total}</span>
                    </div>
                )}
                {readOnly && onReview && it.kind === 'single' && (
                    <div className="hist-card__actions">
                        <button type="button" className="hist-btn hist-btn--ghost" onClick={() => onReview(it.entry)}>Revoir</button>
                    </div>
                )}
                {!readOnly && (
                    <div className="hist-card__actions">
                        {variant === 'rank'
                            ? (it.pct < 80
                                ? <button type="button" className="hist-btn hist-btn--accent" onClick={() => onRefaire(it)}>Refaire</button>
                                : <button type="button" className="hist-btn hist-btn--ghost" onClick={() => onConsulter(it)}>Consulter</button>)
                            : <button type="button" className="hist-btn hist-btn--accent" onClick={() => onConsulter(it)}>Consulter</button>}
                        <button type="button" className="hist-icon-btn" onClick={() => (variant === 'rank' ? onConsulter(it) : onRefaire(it))} title={variant === 'rank' ? 'Consulter' : 'Refaire'}>
                            {variant === 'rank' ? <ChevronRight size={15} /> : <RotateCcw size={14} />}
                        </button>
                    </div>
                )}
            </div>
        );
    };

    // Sous-résultat d'une session groupée (dépliage transversal / SM2 / erreurs).
    const renderHistChild = (ch) => {
        const pct = ch.total > 0 ? Math.round((ch.score / ch.total) * 100) : 0;
        const it = normHistItem({ type: 'single', entry: ch });
        return (
            <div key={ch.id} className={`hist-child ${activeKey === it.key ? 'hist-child--active' : ''}`}>
                <span className="hist-child__pct" style={{ color: scoreColor(pct) }}>{pct}%</span>
                <div className="hist-child__main">
                    <span className="hist-child__title">{ch.sous_sujet_label}</span>
                    {ch.categorie && <span className="hist-child__meta">{ch.categorie}</span>}
                </div>
                <span className="hist-child__frac">{ch.score}/{ch.total}</span>
                {readOnly && onReview && (
                    <div className="hist-child__actions">
                        <button type="button" className="hist-btn hist-btn--ghost hist-btn--xs" onClick={() => onReview(ch)}>Revoir</button>
                    </div>
                )}
                {!readOnly && (
                    <div className="hist-child__actions">
                        <button type="button" className="hist-btn hist-btn--accent hist-btn--xs" onClick={() => onConsulter(it)}>Consulter</button>
                        <button type="button" className="hist-btn hist-btn--ghost hist-btn--xs" onClick={() => onRefaire(it)}>Refaire</button>
                    </div>
                )}
            </div>
        );
    };

    const items = groupHistory(entries).map(normHistItem).filter(it =>
        filter === 'all' || (filter === 'apprentissage' ? it.mode === 'apprentissage' : it.mode !== 'apprentissage'));

    const days = [];
    if (sort === 'recent') {
        const map = {};
        for (const it of items) {
            const k = dayKey(it.created_at);
            if (!map[k]) { map[k] = { key: k, created_at: it.created_at, items: [] }; days.push(map[k]); }
            map[k].items.push(it);
        }
    }
    const ranked = sort === 'score' ? [...items].sort((a, b) => a.pct - b.pct) : [];

    return (
        <div className="sl-historique">
            <div className="hist-toolbar">
                <div className="hist-filters">
                    {[['all', 'Tous'], ['classique', 'Classique'], ['apprentissage', 'Apprentissage']].map(([k, l]) => (
                        <button key={k} type="button" className={`hist-filter ${filter === k ? 'is-active' : ''}`} onClick={() => setFilter(k)}>{l}</button>
                    ))}
                </div>
                <div className="hist-sort">
                    <span className="hist-sort__label">Trier</span>
                    <div className="hist-seg">
                        <button type="button" className={`hist-seg__btn ${sort === 'recent' ? 'is-active' : ''}`} onClick={() => setSort('recent')}>Plus récent</button>
                        <button type="button" className={`hist-seg__btn ${sort === 'score' ? 'is-active' : ''}`} onClick={() => setSort('score')}>Score</button>
                    </div>
                </div>
            </div>

            {items.length === 0 ? (
                <p className="sl-empty" style={{ textAlign: 'center', padding: '28px 0', color: 'var(--text-muted)' }}>
                    Aucun quiz {filter === 'apprentissage' ? 'en apprentissage' : 'classique'} pour le moment.
                </p>
            ) : sort === 'recent' ? (
                days.map(day => {
                    const avg = Math.round(day.items.reduce((s, it) => s + it.pct, 0) / day.items.length);
                    return (
                        <div key={day.key} className="hist-day-block">
                            <div className="hist-day">
                                {(() => {
                                    const h = dayHeading(day.created_at);
                                    const rel = h === "Aujourd'hui" || h === 'Hier';
                                    return <>
                                        <span className="hist-day__label">{h}</span>
                                        {rel && <span className="hist-day__date">{dayShort(day.created_at)}</span>}
                                    </>;
                                })()}
                                <span className="hist-day__line" />
                                <span className="hist-day__stat">{day.items.length} quiz · moy. {avg}%</span>
                            </div>
                            <div className="hist-timeline">
                                {day.items.map(it => (
                                    <div key={it.key} className="hist-node">
                                        <span className="hist-node__dot" style={{ background: scoreColor(it.pct) }} />
                                        {renderHistCard(it, 'timeline', null)}
                                        {it.kind === 'group' && expandedGroups.has(it.group.session_group) && (
                                            <div className="hist-children">{it.group.children.map(ch => renderHistChild(ch))}</div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </div>
                    );
                })
            ) : (
                <>
                    <div className="hist-legend">
                        <span className="hist-legend__end" style={{ color: '#ef4444' }}>À retravailler</span>
                        <span className="hist-legend__bar" />
                        <span className="hist-legend__end" style={{ color: '#10b981' }}>Maîtrisé</span>
                    </div>
                    <div className="hist-rank-list">
                        {ranked.map((it, i) => (
                            <Fragment key={it.key}>
                                {renderHistCard(it, 'rank', i + 1)}
                                {it.kind === 'group' && expandedGroups.has(it.group.session_group) && (
                                    <div className="hist-children hist-children--rank">{it.group.children.map(ch => renderHistChild(ch))}</div>
                                )}
                            </Fragment>
                        ))}
                    </div>
                </>
            )}

            {onClear && (
                <div style={{ textAlign: 'center', marginTop: '24px' }}>
                    <button className="btn btn-sm btn-outline" style={{ color: 'var(--danger)', borderColor: 'var(--danger)' }} onClick={onClear}>
                        <Trash2 size={14} /> Supprimer tout mon historique
                    </button>
                </div>
            )}
        </div>
    );
}
